import React, { useRef, useState } from "react";
import classNames from "classnames";

import styles from "./ImageDropzone.module.scss";

const DEFAULT_HINTS = {
  multiple:
    "Перетащите сюда изображения или нажмите, чтобы выбрать одно или несколько",
  single: "Перетащите сюда изображение или нажмите, чтобы выбрать файл",
};

const isImage = (file) => file.type.startsWith("image/");

// Поле выбора изображений для форм витражных блоков: перетаскивание файлов или выбор по клику.
// onChange(files) — всегда массив File; при multiple = false в нём не больше одного файла.
// hint — подпись в поле, по умолчанию зависит от multiple
const ImageDropzone = ({ onChange, multiple = false, hint, name = "image" }) => {
  const inputRef = useRef(null);
  // dragenter/dragleave приходят и при переходе между дочерними элементами,
  // поэтому считаем вложенность, а не просто переключаем флаг
  const dragDepth = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState(null);

  const selectFiles = (fileList) => {
    const files = Array.from(fileList);
    const images = files.filter(isImage);

    setError(
      images.length < files.length
        ? "Можно загружать только изображения — остальные файлы пропущены"
        : null,
    );

    if (!images.length) {
      return;
    }

    onChange(multiple ? images : images.slice(0, 1));
  };

  const openFileDialog = () => inputRef.current.click();

  const onDragEnter = (e) => {
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current === 0) {
      setIsDragOver(false);
    }
  };

  // Без preventDefault в dragover браузер не разрешит drop и откроет файл во вкладке
  const onDragOver = (e) => e.preventDefault();

  const onDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragOver(false);
    selectFiles(e.dataTransfer.files);
  };

  return (
    <div className={styles.Wrapper}>
      <div
        className={classNames(styles.Dropzone, isDragOver && styles.DragOver)}
        role="button"
        tabIndex={0}
        onClick={openFileDialog}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openFileDialog();
          }
        }}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <span className={styles.Icon} aria-hidden="true">
          +
        </span>
        <span className={styles.Hint}>
          {hint || DEFAULT_HINTS[multiple ? "multiple" : "single"]}
        </span>
      </div>

      <input
        ref={inputRef}
        className={styles.Input}
        type="file"
        name={name}
        accept="image/*"
        multiple={multiple}
        onChange={(e) => {
          selectFiles(e.target.files);
          // Сбрасываем, чтобы повторный выбор того же файла снова вызвал onChange
          e.target.value = "";
        }}
      />

      {error && <p className={styles.Error}>{error}</p>}
    </div>
  );
};

export default ImageDropzone;
